import { useMemo, useEffect, useState } from "react";
import classnames from "classnames";
import { useDeviceInfoQuery } from "@/hooks";
import styles from "@/styles/Card.module.scss";

export function useSliderPosition(
  { index, focusIndex, slidesPerView, activeIndex },
  cardRef
) {
  const [focus, setFocus] = useState(false);
  const { data } = useDeviceInfoQuery({ fetchPolicy: "cache-first" });
  const deviceInfo = data?.deviceInfo;

  const active = useMemo(() => {
    if (deviceInfo?.isMobile || deviceInfo?.isTablet) return false;
    return index >= activeIndex && index < activeIndex + slidesPerView;
  }, [
    index,
    activeIndex,
    slidesPerView,
    deviceInfo?.isMobile,
    deviceInfo?.isTablet,
  ]);

  const feature = useMemo(() => active && index === focusIndex, [
    index,
    focusIndex,
    activeIndex,
    active,
  ]);
  const leftEdge = useMemo(() => active && index === activeIndex, [
    index,
    slidesPerView,
    activeIndex,
    active,
  ]);
  const rightEdge = useMemo(
    () => active && index === activeIndex + slidesPerView - 1,
    [index, slidesPerView, activeIndex, active]
  );

  useEffect(() => {
    const transitionStart = () => setFocus(true);
    const transitionEnd = () => !feature && setFocus(false);
    if (cardRef.current) {
      cardRef.current.addEventListener("transitionstart", transitionStart);
      cardRef.current.addEventListener("transitionend", transitionEnd);
    }
    return () => {
      if (cardRef.current) {
        cardRef.current.removeEventListener("transitionstart", transitionStart);
        cardRef.current.removeEventListener("transitionend", transitionEnd);
      }
    };
  }, [feature, cardRef.current]);

  const classNames = classnames(styles.container, {
    [styles.focus]: focus,
    [styles.feature]: feature,
    [styles.leftEdge]: leftEdge,
    [styles.rightEdge]: rightEdge,
  });
  return { classNames, active };
}
